import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

// Config
import { uiConfig } from '../../../../conf/resourceParameters';

// Components
import Table from '../../../../../components/Table';
import { Button } from '../../../../../components/Button';
// import { ConceptTableModal } from '../conceptTableModal';
import { ResourceParameter } from '../ResourceParameter';
import { DeleteResourceParam } from '../deleteResourceParam';

// Utils
import { translateUIConfigForTable } from '../../../../../common/utility';

const { READ_ONLY_FEATURES } = process.env;

const TableContainer = styled.div`
    margin-left: -1rem;
    margin-right: -1rem;
`;

export const ResourceParametersTable = ({ data, sessionToken, onSave }) => {
    const saveDisabled = READ_ONLY_FEATURES === 'true';
    const [editing, setEditing] = React.useState(null);
    const [deleting, setDeleting] = React.useState(null);
    const { columns, actions } = translateUIConfigForTable(uiConfig, setEditing, setDeleting);
    const items = React.useMemo(() => {
        return data?.criteriaResourceParams ?? [];
    }, [data]);
    const revisedActions = React.useMemo(() => {
        if (!actions || typeof uiConfig.actionConditions !== 'object') {
            return actions;
        }
        return actions.filter(({ type }) => {
            return Object.hasOwn(uiConfig.actionConditions, type) ? uiConfig.actionConditions[type](data) : true;
        });
    }, [actions, data]);
    const controlButtons = [
        <Button key={'add-new'} appearance="primary" onClick={() => setEditing('new')}>
            Add Parameter
        </Button>
    ];

    const selectedItem = React.useMemo(() => {
        if (!editing) return null;
        return (items ?? []).find((item) => {
            return item[uiConfig.primaryKey] === editing;
        });
    }, [items, editing]);

    const tableData = React.useMemo(() => {
        if (!items) return [];
        const transformedItems =
            revisedActions != null
                ? items.map((v) => ({
                      ...v,
                      DataTableActions: revisedActions.map((i) => {
                          return {
                              ...i,
                              action: () => i.action(v[uiConfig.primaryKey])
                          };
                      })
                  }))
                : items;
        return transformedItems.filter(({ dtoState }) => dtoState !== 'DELETED');
    }, [items, revisedActions]);

    const deleteData = React.useMemo(() => {
        if (!deleting) return null;
        return items.find((item) => item[uiConfig.primaryKey] === deleting);
    }, [deleting]);

    const onDelete = (newParameter) => {
        const itemsCopy = [...items];
        const itemIndex = itemsCopy.findIndex(({ uuid }) => uuid === newParameter.uuid);

        if (itemIndex >= 0) {
            itemsCopy[itemIndex] = {
                ...newParameter,
                dtoState: 'DELETED'
            };
        }

        onSave({
            criteriaResourceParams: itemsCopy,
            dtoState: 'UPDATED'
        });
    };

    // const isExistingDoc = (doc) => {
    //     console.log('doc', doc);
    //     return (
    //         (items ?? []).find((v) => {
    //             return v.paramId !== doc.paramId && v.resourceId === doc.resourceId && v.name === doc.name;
    //         }) !== undefined
    //     );
    // };

    return (
        <TableContainer>
            <Table
                withHeader={true}
                columns={columns}
                data={tableData}
                withActions={revisedActions !== null}
                id={'resource-parameters-manager'}
                label={'Resource Parameters'}
                showControlDeck={true}
                paginated={true}
                controlButtons={controlButtons}
            />
            {(selectedItem !== null || editing === 'new') && (
                <ResourceParameter
                    isNew={editing === 'new'}
                    parameterId={data?.parameterId}
                    saveDisabled={saveDisabled}
                    data={selectedItem}
                    onApply={(newParameter) => {
                        const itemsCopy = [...items];
                        const itemIndex = itemsCopy.findIndex(({ uuid }) => uuid === newParameter.uuid);

                        if (itemIndex >= 0) {
                            itemsCopy[itemIndex] = newParameter;
                        } else {
                            itemsCopy.push(newParameter);
                        }
                        onSave({
                            criteriaResourceParams: itemsCopy,
                            dtoState: 'UPDATED'
                        });
                    }}
                    // isExistingDoc={isExistingDoc}
                    closeDialog={() => {
                        setEditing(null);
                    }}
                    sessionToken={sessionToken}
                    onMount={typeof uiConfig.onFormMount === 'function' && uiConfig.onFormMount}
                />
            )}
            {deleting != null && (
                <DeleteResourceParam
                    data={deleteData}
                    onDelete={onDelete}
                    closeDialog={() => setDeleting(null)}
                    sessionToken={sessionToken}
                />
            )}
        </TableContainer>
    );
};

export default ResourceParametersTable;

ResourceParametersTable.propTypes = {
    data: PropTypes.object,
    items: PropTypes.arrayOf(PropTypes.object),
    sessionToken: PropTypes.string,
    onSave: PropTypes.func
};
